import { useMemo } from 'react';
import { Molecule } from '../../types';
import { calculateMolecularProperties } from '../../utils/properties';
import { Beaker, Activity, Shield, Zap, Droplets, Scale } from 'lucide-react';

interface PropertyDashboardProps {
  molecule: Molecule;
}

export function PropertyDashboard({ molecule }: PropertyDashboardProps) {
  const properties = useMemo(() => {
    return calculateMolecularProperties(molecule);
  }, [molecule]);

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-primary-400" />
          <h3 className="card-title">Molecular Properties</h3>
        </div>
        <p className="card-subtitle">{molecule.name || 'Unknown Molecule'}</p>
      </div>

      <div className="card-body">
        {/* Basic Properties */}
        <div className="input-section">
          <label className="input-section-title flex items-center gap-2">
            <Beaker className="w-4 h-4" />
            Basic Properties
          </label>
          <div className="property-grid" style={{ marginTop: '0.75rem' }}>
            <div className="property-card">
              <span className="property-label">Molecular Weight</span>
              <span className="property-value">
                {properties.molecularWeight.toFixed(2)}
                <span className="property-unit">Da</span>
              </span>
            </div>
            <div className="property-card">
              <span className="property-label">Formula</span>
              <span className="property-value">
                {properties.formula}
              </span>
            </div>
            <div className="property-card">
              <span className="property-label">Atoms</span>
              <span className="property-value">
                {properties.atomCount}
              </span>
            </div>
            <div className="property-card">
              <span className="property-label">Bonds</span>
              <span className="property-value">
                {properties.bondCount}
              </span>
            </div>
          </div>
        </div>

        {/* Drug Likeness */}
        <div className="input-section" style={{ marginTop: '1.5rem' }}>
          <label className="input-section-title flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Drug Likeness
          </label>
          <div className="lipinski-grid" style={{ marginTop: '0.75rem' }}>
            <div className={`lipinski-item ${properties.molecularWeight <= 500 ? 'passed' : 'violated'}`}>
              <span className="lipinski-value">{properties.molecularWeight.toFixed(0)}</span>
              <span className="lipinski-criteria">MW {'<'} 500</span>
            </div>
            <div className={`lipinski-item ${properties.logP <= 5 ? 'passed' : 'violated'}`}>
              <span className="lipinski-value">{properties.logP.toFixed(2)}</span>
              <span className="lipinski-criteria">LogP {'<'} 5</span>
            </div>
            <div className={`lipinski-item ${properties.hydrogenBondDonorCount <= 5 ? 'passed' : 'violated'}`}>
              <span className="lipinski-value">{properties.hydrogenBondDonorCount}</span>
              <span className="lipinski-criteria">HBD {'<'} 5</span>
            </div>
            <div className={`lipinski-item ${properties.hydrogenBondAcceptorCount <= 10 ? 'passed' : 'violated'}`}>
              <span className="lipinski-value">{properties.hydrogenBondAcceptorCount}</span>
              <span className="lipinski-criteria">HBA {'<'} 10</span>
            </div>
          </div>
          
          <div className="drug-score" style={{ marginTop: '1rem' }}>
            <span className="drug-score-value">{properties.drugLikenessScore}</span>
            <div>
              <span className="drug-score-label">Drug Likeness Score</span>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
                {properties.drugLikenessClass === 'drug-like' && '✅ Passes Lipinski\'s Rule of Five'}
                {properties.drugLikenessClass === 'borderline' && '⚠️ Borderline drug-likeness'}
                {properties.drugLikenessClass === 'non-drug-like' && '❌ Fails drug-likeness criteria'}
              </p>
            </div>
          </div>
        </div>

        {/* Physicochemical Properties */}
        <div className="input-section" style={{ marginTop: '1.5rem' }}>
          <label className="input-section-title flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Physicochemical
          </label>
          <div className="property-grid" style={{ marginTop: '0.75rem' }}>
            <div className="property-card">
              <span className="property-label">LogP</span>
              <span className="property-value">{properties.logP.toFixed(2)}</span>
            </div>
            <div className="property-card">
              <span className="property-label">TPSA</span>
              <span className="property-value">
                {properties.topologicalPolarSurfaceArea.toFixed(1)}
                <span className="property-unit">Å²</span>
              </span>
            </div>
            <div className="property-card">
              <span className="property-label">Rotatable Bonds</span>
              <span className="property-value">{properties.rotatableBondCount}</span>
            </div>
            <div className="property-card">
              <span className="property-label">Rings</span>
              <span className="property-value">{properties.ringCount}</span>
            </div>
          </div>
        </div>

        {/* Composition */}
        <div className="input-section" style={{ marginTop: '1.5rem' }}>
          <label className="input-section-title flex items-center gap-2">
            <Droplets className="w-4 h-4" />
            Atom Composition
          </label>
          <div style={{ marginTop: '0.75rem' }}>
            {Object.entries(properties.atomComposition || {}).map(([element, count]) => (
              <div key={element} className="property-row">
                <span className="property-row-label">{element}</span>
                <span className="property-row-value">{count} atom{count !== 1 ? 's' : ''}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

